import React, { useState, useRef, useEffect } from "react";
import { Menu, MenuItem, MenuButton, SubMenu } from "@szhsin/react-menu";
import "@szhsin/react-menu/dist/index.css";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";

const StyledMenuButton = styled(MenuButton)({
  background: "#6860EE",
  color: "white",
  width: "100%",
  padding: "10px 16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  cursor: "pointer",
  borderRadius: "8px",
  // marginRight: "15px",
  border: "none",
  fontSize: "15px",
  "&.create-button": {
    background: "#6860EE",
    color: "white",
    fontWeight: "600",
    padding: "10px 16px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    transition: "all 0.3s ease",
    "&:hover": {
      background: "#5c47ce",
      transform: "scale(1.05)"
    }
  }
});
const StyledHeadingSpan = styled("span")({
  display: "flex",
  flexGrow: 1,
  textAlign: "center",
});
const StyledMenu = styled(Menu)({
  ".szh-menu": {
    padding: "4px 2px",
    width: "100%",
    borderRadius: "12px",
    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
    border: "1px solid var(--border__color)",
    overflow: "hidden",
    backgroundColor: "var(--body__background)",
  },
  "& .react-menu__menu": {
    position: "relative",
    minWidth: "100%",
  },
  ".szh-menu__item": {
    padding: "0.375rem 1.5rem",
  },
});

const StyledMenuItem = styled(MenuItem)({
  padding: "8px 16px",
  cursor: "pointer",
  fontSize: "15px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  backgroundColor: "var(--body__background)",
  color: "var(--header__color)",
  "&:hover": {
    backgroundColor: "#6860EE",
    color: "white",
  },
  "&.disabled": {
    cursor: "not-allowed",
    opacity: 0.5,
    color: "#999",
    "&:hover": {
      backgroundColor: "var(--body__background)",
      color: "#999",
    },
  },
});

const StyledSubMenu = styled(SubMenu)({
  "& .react-menu__menu": {
    position: "absolute",
    left: "100%",
    top: "0",
    backgroundColor: "var(--body__background)",
    color: "var(--header__color)",
    borderRadius: "5px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
  "& .react-menu__item--hover": {
    backgroundColor: "#1b52a7",
    color: "white",
  },
  "& .szh-menu__item": {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    padding: "8px 16px",
    boxSizing: "border-box",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#1b52a7",
      color: "white",
    },
  },
});

const StyledDropDownContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  position: "relative",
  zIndex: "99",
});
const StyledParentDiv = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
  boxSizing: "border-box",
  cursor: "pointer",
  paddingLeft: "6px",
});

function PageSelector({
  dropdownHeading = "",
  options = [],
  onOptionSelect,
  iconSrc,
  iconPosition = "start",
  iconWidth = "20px",
  className = "",
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
      updateMenuWidth();
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const updateMenuWidth = () => {
    if (buttonRef.current && dropdownRef.current) {
      const buttonWidth = buttonRef.current.getBoundingClientRect().width;
      const menuElement = dropdownRef.current.querySelector('.react-menu__menu');
      if (menuElement) {
        menuElement.style.width = `${buttonWidth}px`;
      }
    }
  };

  useEffect(() => {
    if (isMenuOpen) {
      setTimeout(updateMenuWidth, 0);
    }
  }, [isMenuOpen]);

  const handleOptionClick = (option) => {
    if (option.disabled) {
      return; 
    }
    if (option.path) navigate(option.path);
    if (option.onClick) option.onClick();

    if (!option.children) {
      setIsMenuOpen(false);
      if (onOptionSelect) onOptionSelect(option);
    }
  };

  const handleSubMenuIconClick = (event, option) => {
    event.stopPropagation();
    if (screenWidth < 991) {
      setIsMenuOpen((prev) => !prev);
    } else {
      if (option.path) navigate(option.path);
      if (option.onClick) option.onClick();
      if (!option.path && option.children) setIsMenuOpen(true);
      else setIsMenuOpen(false);
    }
  };

  const renderOptions = (options) => {
    return options.map((option, index) =>
      option.children ? (
        <StyledSubMenu
          key={index}
          label={
            <StyledParentDiv
              onClick={(e) => {
                handleOptionClick(option);
                handleSubMenuIconClick(e, option);
              }}
            >
              {option.name}
            </StyledParentDiv>
          }
        >
          {renderOptions(option.children)}
        </StyledSubMenu>
      ) : (
        <StyledMenuItem 
          key={index} 
          onClick={() => handleOptionClick(option)}
          className={option.disabled ? "disabled" : ""}
        >
          {option.name}
        </StyledMenuItem>
      )
    );
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <StyledDropDownContainer ref={dropdownRef}>
      <StyledMenu
        menuButton={
          <StyledMenuButton 
            ref={buttonRef}
            onClick={() => setIsMenuOpen((prev) => !prev)}
            className={className}
          >
            {iconPosition === "start" && iconSrc && (
              <img
                src={iconSrc}
                alt="icon"
                style={{ width: iconWidth, marginRight: "8px" }}
              />
            )}
            <StyledHeadingSpan>{dropdownHeading}</StyledHeadingSpan>
            {iconPosition === "end" && iconSrc && (
              <img
                src={iconSrc}
                alt="icon"
                style={{ width: iconWidth, marginLeft: "8px" }}
              />
            )}
            {isMenuOpen ? (
              <ExpandLessIcon
                sx={{
                  color: "#fff",
                }}
              />
            ) : (
              <ExpandMoreIcon
                sx={{
                  color: "#fff",
                }}
              />
            )}
          </StyledMenuButton>
        }
        onMenuChange={(e) => {
          setIsMenuOpen(e.open);
          if (e.open) {
            setTimeout(updateMenuWidth, 0);
          }
        }}
      >
        {renderOptions(options)}
      </StyledMenu>
    </StyledDropDownContainer>
  );
}

export default PageSelector;
